import type { CarProfile, EngineLoopSample } from './CarProfile';

const BASE = '/audio/ferrari-458';

/**
 * Ferrari 458 Italia — 4.5 litres, eight cylinders, nine thousand revs and no turbo in the way.
 *
 * ON SALE since 2026-09-02, ON MARC'S INSTRUCTION AND ON HIS LICENCE. The bank behind this car began
 * as an Assetto Corsa sound mod; Marc states he has since acquired the rights to it, exploitation
 * included, and asked for the car to go live. `adminOnly: false` therefore lets it into dist/cars.json,
 * which is the file the server reads to decide who may open a checkout and whose audio it will sign a
 * grant for. docs/NEW-CAR.md section 0 forbids shipping sim-ripped banks; this is the documented
 * exception to that rule. KEEP THE PAPERWORK FILED AND FINDABLE — it is the only thing that makes the
 * sentence above true, and the car should go back to `adminOnly: true` the day it cannot be produced.
 *
 * Every rung below is a rung somebody actually recorded, labelled with the rpm the bank's own FMOD
 * metadata stores for it. loopStart/loopEnd are the seams tools/loop-points.py measured.
 */
const loop = (id: string, file: string, recordedRpm: number, loopStart?: number, loopEnd?: number): EngineLoopSample => ({
  id,
  file,
  recordedRpm,
  loopStart,
  loopEnd,
});

// EXHAUST — engine_ext, the car from outside. Base voice, left slider (EXH).
// Every rpm here is the author's own FMOD root, not his file name: the two disagree on purpose and the
// root is where he plays the take. See src/cars/lamborghini-aventador.ts for the whole story.
const onLoops: EngineLoopSample[] = [
  // THE IDLES RUN LONG NOW. Marc: "el idle se nota q cada 2 segundos se repite". The cabin idle on
  // the SVJ looped over 1.89 s of a five-second take - almost exactly the two seconds he counted -
  // and every other idle in the three cars was between 2.8 and 3.7. Re-fitted with the loop-point
  // search widened to the ends of the file (start in the first 0.75 s, end in the last 0.1) and
  // scored on the same normalised match: 4.1 to 4.8 seconds each, and every seam scores BETTER than
  // the one it replaces (the 812's cabin idle went 0.53 -> 0.94).
  loop('idle', 'idle', 950, 0.3550, 4.5700),
  loop('on_1000', 'on_1000', 1015, 0.3837, 1.6339),
  loop('on_1250', 'on_1250', 1225, 0.4395, 2.5817),
  loop('on_1500', 'on_1500', 1490, 1.0223, 2.4237),
  loop('on_2000', 'on_2000', 1635, 0.8300, 2.1772),
  loop('on_2500', 'on_2500', 2400, 0.1996, 2.9409),
  loop('on_3000', 'on_3000', 2705, 0.1707, 2.9032),
  loop('on_4000', 'on_4000', 3605, 0.9968, 2.3129),
  loop('on_5000', 'on_5000', 3780, 0.0439, 2.9536),
  loop('on_6000', 'on_6000', 5140, 0.1437, 2.0725),
  loop('on_7000', 'on_7000', 5595, 0.0290, 2.2295),
  loop('on_8000', 'on_8000', 7168, 0.3641, 2.9653),
  loop('on_9000', 'on_9000', 8229, 1.0494, 2.2496),
];

const offLoops: EngineLoopSample[] = [
  loop('off_idle', 'idle', 950, 0.3550, 4.5700),
  loop('off_1000', 'off_1000', 1040, 0.1851, 1.8217),
  loop('off_1250', 'off_1250', 1460, 0.6762, 2.0694),
  loop('off_1500', 'off_1500', 1820, 1.0296, 2.6499),
  loop('off_2500', 'off_2500', 2505, 0.0319, 2.3918),
  loop('off_3000', 'off_3000', 3075, 0.0466, 1.9832),
  loop('off_4000', 'off_4000', 3825, 0.0853, 2.9756),
  loop('off_5000', 'off_5000', 4935, 0.0473, 2.6985),
];

// CABIN — engine_int, the onboard. Overlay, right slider (CABIN), silent until you bring it up.
const cabinOn: EngineLoopSample[] = [
  loop('c_idle', 'int_idle', 950, 0.1600, 4.9400),
  loop('c_on_1000', 'int_on_1000', 1040, 0.4430, 1.7459),
  loop('c_on_1250', 'int_on_1250', 1250, 1.0445, 2.3735),
  loop('c_on_1500', 'int_on_1500', 1495, 1.0371, 2.9288),
  loop('c_on_2000', 'int_on_2000', 1660, 0.3231, 2.9442),
  loop('c_on_2500', 'int_on_2500', 2380, 0.1358, 2.9542),
  loop('c_on_3000', 'int_on_3000', 2710, 0.0341, 2.8969),
  loop('c_on_4000', 'int_on_4000', 3610, 0.6351, 2.0791),
  loop('c_on_6000', 'int_on_6000', 5120, 0.6578, 2.9718),
  loop('c_on_9000', 'int_on_9000', 8330, 0.9305, 2.4220),
];

const cabinOff: EngineLoopSample[] = [
  loop('c_off_idle', 'int_idle', 950, 0.1600, 4.9400),
  loop('c_off_1000', 'int_off_1000', 1050, 0.2296, 1.6994),
  loop('c_off_1250', 'int_off_1250', 1500, 0.3695, 1.7565),
  loop('c_off_1500', 'int_off_1500', 1690, 0.5199, 2.6769),
  loop('c_off_2500', 'int_off_2500', 2440, 0.1179, 1.9857),
  loop('c_off_3000', 'int_off_3000', 3010, 0.9020, 2.9555),
  loop('c_off_4000', 'int_off_4000', 3769, 0.4967, 2.8129),
  loop('c_off_5000', 'int_off_5000', 4845, 0.0212, 2.9785),
  loop('c_off_7000', 'int_off_7000', 6442, 0.4597, 2.5152),
];

// THE SIMULATOR ITSELF — the stock Assetto Corsa bank, third slider (SIM). Not part of the car's voice:
// it is here so the mod can be judged against what it replaced instead of against silence. Starts at 0.
const simLoops: EngineLoopSample[] = [
  loop('s_idle', 'sim_idle', 950, 0.2450, 4.1600),
  loop('s_on_2986', 'sim_on_2986', 2986, 1.0438, 2.2969),
  loop('s_on_4682', 'sim_on_4682', 4682, 1.0269, 2.9512),
  loop('s_on_5127', 'sim_on_5127', 5127, 0.5457, 2.9324),
  loop('s_on_7126', 'sim_on_7126', 7126, 0.0483, 2.6437),
  loop('s_on_8276', 'sim_on_8276', 8276, 0.2611, 1.9777),
  loop('s_on_8396', 'sim_on_8396', 8396, 0.7217, 2.5517),
];

const simOffLoops: EngineLoopSample[] = [
  loop('s_off_idle', 'sim_idle', 950, 0.2450, 4.1600),
  loop('s_off_1453', 'sim_off_1453', 1453, 0.0902, 2.0166),
  loop('s_off_4017', 'sim_off_4017', 4017, 0.2006, 2.1787),
  loop('s_off_4966', 'sim_off_4966', 4966, 0.6054, 2.9192),
  loop('s_off_6540', 'sim_off_6540', 6540, 0.8495, 2.3364),
  loop('s_off_7952', 'sim_off_7952', 7952, 0.0432, 2.7380),
];

// THE FOURTH POSITION — engine_ext_distance, the car heard from a distance. Its own fader (FAR), silent until asked for.
const extraOn: EngineLoopSample[] = [
  loop('x_on_1250', 'x_on_1250', 877, 0.9107, 2.5669),
  loop('x_idle', 'x_idle', 950, 0.1900, 4.7600),
  loop('x_on_1500', 'x_on_1500', 1052, 0.0614, 2.9027),
  loop('x_on_2000', 'x_on_2000', 1336, 0.5676, 2.6673),
  loop('x_on_2500', 'x_on_2500', 1667, 0.3716, 2.2552),
  loop('x_on_3000', 'x_on_3000', 1824, 0.0576, 2.6633),
  loop('x_on_4000', 'x_on_4000', 2404, 0.1851, 2.7591),
  loop('x_on_5000', 'x_on_5000', 2673, 0.1595, 2.8951),
  loop('x_on_6000', 'x_on_6000', 3231, 0.9016, 2.7344),
  loop('x_on_7000', 'x_on_7000', 3574, 0.5887, 2.7767),
];

const extraOff: EngineLoopSample[] = [
  loop('x_off_idle', 'x_idle', 950, 0.1900, 4.7600),
  loop('x_off_1355', 'x_off_1355', 1351, 0.7460, 1.9655),
  loop('x_off_1500', 'x_off_1500', 1500, 0.2310, 1.9216),
  loop('x_off_2555', 'x_off_2555', 2578, 0.2277, 2.9505),
  loop('x_off_3640', 'x_off_3640', 3673, 0.6675, 2.9695),
  loop('x_off_4660', 'x_off_4660', 4705, 0.4785, 1.9970),
  loop('x_off_5250', 'x_off_5250', 5298, 0.0956, 2.5743),
  loop('x_off_7065', 'x_off_7065', 7107, 0.0550, 2.2024),
  loop('x_off_8070', 'x_off_8070', 8130, 0.3687, 2.8284),
];

// PART THROTTLE — the middle of the load axis, one ladder per microphone. Between lifting off and
// flooring it, which is nearly all of the driving, these are the takes the car actually made; without
// them the pedal only ever blends a scream with an overrun.
// PART THROTTLE. Same fault the 812 had, on the car Marc said sounded worst: this ladder was built
// from `engine_ext_on_throttle_*`, which are 180 to 280 MILLISECOND throttle transients, looped as a
// sustained layer. The bank ships eight `engine_ext_steady_*` and eight `engine_int_steady_*` takes,
// four to ten seconds each, and none of them were used.
//
// The exterior rungs carry the author's own FMOD roots and the cabin ones carry his file names, and
// the two agree: measured against the stock Kunos ladder every root-labelled rung reads 0.990 to
// 1.001 of its label, and the cabin's two trusted rungs read 1.004 and 0.964. One rung is dropped -
// the root for `engine_ext_steady_7000` is 2670, which contradicts its own name by a mile and could
// not be measured with any confidence. This car has caught me out on labels once already.
const midLoops: EngineLoopSample[] = [
  loop('m_2975', 'm_on_2975', 2975, 0.6566, 1.9531),
  loop('m_3170', 'm_on_3170', 3170, 0.9424, 2.6539),
  loop('m_3990', 'm_on_3990', 3990, 0.2712, 2.9144),
  loop('m_4230', 'm_on_4230', 4230, 0.1557, 1.9505),
  loop('m_5040', 'm_on_5040', 5040, 0.4832, 2.9695),
  loop('m_5820', 'm_on_5820', 5820, 1.0038, 2.2874),
  loop('m_7390', 'm_on_7390', 7390, 0.6301, 2.8314),
];

const cabinMid: EngineLoopSample[] = [
  loop('cm_2000', 'im_on_2000', 2000, 0.6952, 2.9385),
  loop('cm_2500', 'im_on_2500', 2500, 0.4591, 2.6452),
  loop('cm_3000', 'im_on_3000', 3000, 0.8274, 2.2035),
  loop('cm_4000', 'im_on_4000', 4000, 0.3662, 2.2643),
  loop('cm_5000', 'im_on_5000', 5000, 0.4993, 2.1711),
  loop('cm_6000', 'im_on_6000', 6000, 0.8777, 2.1627),
  loop('cm_7000', 'im_on_7000', 7000, 0.7031, 2.1060),
  loop('cm_8000', 'im_on_8000', 8000, 0.6817, 1.9902),
];

export const ferrari458: CarProfile = {
  // The four-bank build is the REFERENCE copy now: the car people drive is the bounce of it in
  // src/cars/single/, which carries the plain id, slug and name. This one keeps the same audio
  // folder and the four faders, for A/B and for the SOLO buttons.
  id: 'ferrari-458-4v',
  slug: '570-hp-v8-4v',
  name: '570 HP V8 4 VOICES',
  manufacturer: 'Supercar · Italy',
  engineType: '4.5 L V8 · 8,500 rpm',
  tagline: 'The last atmospheric V8 before the turbos arrived.',
  // MOVED OFF THE F1 STYLE (2026-08-31). It was showing a 2004 Formula 1 steering-wheel display,
  // red seven-segment on black, which is not this car by any stretch: a 458 Italia has the same
  // yellow analogue tachometer family as the 812, flanked by two round screens. This puts it on
  // THE SAME HUD AS THE 812, VERBATIM (Marc: "copy paste mismo HUD q el 812"). This car used to
  // carry the 812's instrument in a 458 livery I had built from one photograph: a canary face, black
  // numerals, a dark horse and a dark ladder. It goes: same olive face, same cream numerals, same
  // cream horse and ladder, same brick gear window with the gear in orange. The printed marks come
  // over too, and they are honest here - this car's red line is 8500 as well.
  hud: {
    dialMarks: [7500, 8500], accent: '#d40000', accent2: '#e8c93a', style: 'f812', brand: '#ffd400',
    dial: {
      face: ['#d8b03c', '#bb932a', '#8a6b18', '#513c0b'],
      num: '#f6ecce', numRed: '#ff7a18',
      badge: ['#56281f', '#33170f'], badgeText: '#eb6e28', badgeSub: '#ded4c4',
      horse: '#f3ead6',
      ladder: 'rgba(252,242,210,0.78)',
    },
  },
  photo: { src: '/cars/570-hp-v8.jpg?v=1', pos: '50% 50%', credit: '' },   // generated (Nano Banana, 2026-09-14, Marc's take): our own image, no photographer, no badge, no factory light signature
  available: true,
  adminOnly: false,  // LIVE 2026-09-02 (Marc: bank bought, exploitation rights included)
  realTopSpeedKmh: 325,

  idleRpm: 950,
  maxRpm: 9000,
  redlineRpm: 8500,
  // AUDITED 2026-08-31 (Marc: "creo q está mal... hay algunas marchas más largas q otras").
  // He was right. A real gearbox's steps shrink as you go up, and these did not: 5th->6th was 1.303, LONGER than 4th->5th at 1.233.
  // The ladder is refitted as a geometric progression that keeps BOTH ends - first gear and the
  // car's top speed are untouched - and lands on a 1.20 overdrive step at the top, so every step
  // now falls. gearTopSpeedsKmh is the speed at the LIMITER in each gear, not a cruising speed.
  // THE PORSCHE'S SHAPE, WHICH IS THE ONE THAT WORKS (Marc: "pq no las dejas todas como el porsche??
  // esq 60km en primera y luego 17km/h hasta la segunda me parece una puta locura"). He is right, and
  // the mistake was mine: I narrowed the RATIOS, and even ratios on a 7-speed give uneven SPEEDS -
  // first covering 60 km/h and second 17. What the GT3 Cup does is the other way round: first takes
  // 0.323 of the car's top speed and every gear after it covers the same slice, 27 km/h apiece on a
  // 200 km/h car. Same rule here with seven gears: first to 0.323, then six equal slices of 22.5.
  // Every step still falls (1.35 down to 1.13) and the top gear still ends on the car's own top speed.
  gearTopSpeedsKmh: [105, 142, 178, 215, 252, 288, 325],

  shift: { autoUpshiftRpm: 8800, partThrottleUpshiftRpm: 6200, brakingDownshiftMarginRpm: 200, autoDownshiftRpm: 3200, shiftTimeMs: 60, minShiftIntervalMs: 250, blipStrength: 0.7 },
  engine: { inertiaUpRpmPerS: 14000, inertiaDownRpmPerS: 11000, freeRevUpRpmPerS: 12000, freeRevDownRpmPerS: 7000 },
  audio: {
    // 1.15, from 0.9 (Marc, in the Tesla: 'el master volume, súbelo un poco'); the compressor holds the peaks
    masterGain: 1.15,
    voice: 'clean',   // play it the way the game does: crossfade + autopitch, nothing on top
    onMixFloor: 0,   // AC does a full ON/OFF swap: the coast rungs ARE the lift-off sound
    liftOffLowpassHz: 3200,
    envelopeTauS: 0.035,
    procedural: { cylinders: 8, brightness: 0.72, roughness: 0.28, noise: 0.22 },
  },

  samples: {
    basePath: BASE,
  // THE MIX IS FIXED HERE NOW (Marc: "fija ya los volumenes en los 3 coches!!! segun los q te
  // dije"). These are the fader positions off his own screenshots, read at the slider's scale
  // (VOL 0..1, every voice 0..1.5), so the car opens on the mix he settled on instead of on one
  // voice with the other three at zero. VOL is not a car setting and is left alone.
    on: onLoops,
    off: offLoops,
    mid: midLoops,
    // Two microphones of the same drive, one slider each (EXH / CABIN in the pit box). The exhaust is
    // the base voice at full and the onboard rides the overlay at 0 until you bring it up. `gate: 'none'`
    // because this ladder owns an idle, unlike the 911's overlay.
    overlay: { gain: 1.45, baseGain: 0.83, gate: 'none', baseLabel: 'EXH', label: 'CABIN', on: cabinOn, off: cabinOff, mid: cabinMid },
    sim: { on: simLoops, off: simOffLoops, gain: 1.45, label: 'SIM' },
    extra: { on: extraOn, off: extraOff, gain: 0.05, label: 'FAR' },
    limiter: { id: 'limiter', file: 'limiter', recordedRpm: 9000 },
    shiftUp: [{ id: 'shift_up', file: 'shift_up' }],
    shiftDown: [{ id: 'shift_down', file: 'shift_down' }],
    backfire: [{ id: 'backfire_1', file: 'backfire_1' }, { id: 'backfire_2', file: 'backfire_2' }, { id: 'backfire_3', file: 'backfire_3' }, { id: 'backfire_4', file: 'backfire_4' }, { id: 'backfire_5', file: 'backfire_5' }],
    doorOpen: { id: 'door_open', file: 'door_open' },
    doorShut: { id: 'door_shut', file: 'door_shut' },
    // ENG START: a real 458 being started, cut by tools/start-clip.py. Not resampled, on purpose —
    // see `start` below.
    starter: { id: 'starter', file: 'starter' },
    // THE SHOWROOM instead of a pit box: the Italian bed and this car's own storyteller, cut by
    // tools/showroom-clip.py. Parked with the engine off, now and then, quietly.
    // THE STORYTELLER IS OFF (2026-09-14): the take on disk speaks the marque and the model name aloud,
    // and the line-up went generic. The music bed stays. Generic scripts are in docs/SHOWROOM-SCRIPTS.md;
    // once a take is cut, put `story: { id: 'story', file: 'story' }` back here.
    showroom: { music: { id: 'showroom', file: 'showroom' } },
  },
  // THE COLD FAST IDLE IS THE WHOLE POINT HERE. That take catches at ~1800 rpm and then sits at 1370
  // for as long as the camera runs, while this car idles at 950: four semitones apart, so pitching the
  // recording down to meet the idle would have turned a Ferrari into a tractor. The car goes up to meet
  // the recording instead and eases down to 950 afterwards over a few seconds — which is exactly what a
  // cold V8 does, and it is now audible. The hold runs past the end of the clip (8.7 s) so the whole
  // crossfade happens at one rpm.
  start: { fireAtS: 1.06, catchRpm: 1800, catchTauS: 0.75, fastIdleRpm: 1370, holdS: 9.0, easeS: 2.6, duckAfterS: 1.4 },
};
